import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ShieldCheck } from "lucide-react";
import { useAdminAuth } from "@/state/AdminAuthContext";
import { ADMIN_LOGIN } from "@/constants/testIds/admin";

const genericError = "Log masuk tidak dapat diproses. Sila cuba lagi.";

export default function AdminLoginPage() {
  const { login, status } = useAdminAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (status === "authenticated") navigate("/admin", { replace: true });
  }, [status, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      navigate("/admin", { replace: true });
    } catch (err) {
      const code = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (code === 429) {
        setError("Terlalu banyak percubaan. Sila cuba semula sebentar lagi.");
      } else if (code === 401 && typeof detail === "string") {
        setError(detail);
      } else {
        setError(genericError);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen grid place-items-center bg-[#0b1f18] px-4"
      data-testid={ADMIN_LOGIN.page}
    >
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center text-center">
          <span className="grid place-items-center h-12 w-12 rounded-2xl bg-gradient-to-b from-[#12876a] to-[#0f3f2f] text-[#c79a3b] ring-1 ring-[#c79a3b]/40">
            <ShieldCheck strokeWidth={1.75} className="h-6 w-6" />
          </span>
          <h1 className="mt-5 font-heading text-2xl font-extrabold text-white">
            KadBisnes.my
          </h1>
          <p className="mt-1 text-sm uppercase tracking-[0.25em] text-[#c79a3b]">
            Admin
          </p>
        </div>

        <form
          onSubmit={submit}
          className="mt-8 rounded-3xl bg-white/[0.04] ring-1 ring-white/10 backdrop-blur p-6 sm:p-7 space-y-5"
        >
          <div className="space-y-1.5">
            <label
              htmlFor="admin-email"
              className="text-xs font-semibold uppercase tracking-wide text-[#a9c4b8]"
            >
              Emel
            </label>
            <input
              id="admin-email"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid={ADMIN_LOGIN.emailInput}
              className="w-full rounded-xl bg-[#0b1f18] text-white placeholder-[#5c7268] ring-1 ring-white/15 focus:ring-2 focus:ring-[#c79a3b] outline-none px-4 py-3 text-sm transition"
            />
          </div>

          <div className="space-y-1.5">
            <label
              htmlFor="admin-password"
              className="text-xs font-semibold uppercase tracking-wide text-[#a9c4b8]"
            >
              Kata Laluan
            </label>
            <input
              id="admin-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid={ADMIN_LOGIN.passwordInput}
              className="w-full rounded-xl bg-[#0b1f18] text-white placeholder-[#5c7268] ring-1 ring-white/15 focus:ring-2 focus:ring-[#c79a3b] outline-none px-4 py-3 text-sm transition"
            />
          </div>

          {error && (
            <p
              className="text-sm text-[#ffb4a2] bg-[#3a1512] ring-1 ring-[#ff6b6b]/30 rounded-xl px-3 py-2"
              data-testid={ADMIN_LOGIN.error}
              role="alert"
            >
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            aria-busy={loading}
            data-testid={ADMIN_LOGIN.submitButton}
            className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-b from-[#e0b455] to-[#c79a3b] text-[#0b1f18] font-bold px-4 py-3 text-sm transition hover:brightness-105 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {loading && <Loader2 strokeWidth={2} className="h-4 w-4 animate-spin" />}
            {loading ? "Sedang log masuk..." : "Log Masuk"}
          </button>
        </form>
      </div>
    </div>
  );
}
